import './style.scss'
import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {DateRange } from 'react-day-picker'
import 'react-day-picker/dist/style.css'

import {
    Box,
    Button,
    FilterDropdown,
    InstitutionTable,
    SearchField,
    TablePagination,
    Text,
    Toolbar,
    Flex, TableColumn,
    tokens, DateRangeFilterButton,
} from 'src/componentv2'

import downloadIcon from 'src/assets/v2/img/download_icon.svg'

import ConfigurationApiContext, { InstitutionListItem, InstitutionsMeta } from 'src/provider/API/v2/call-service/configuration'
import { buildInstitutionsCsv, downloadCsv } from 'src/utils/csvExport'

export interface InstitutionListProps {
    onCreate?: () => void
}

const FILTER_OPTIONS = [
    { label: 'Active', value: 'isActive:true' },
    { label: 'Inactive', value: 'isActive:false' },
    { label: 'Has Web', value: 'hasWeb:true' },
    { label: 'No Web', value: 'hasWeb:false' },
    { label: 'Has Mobile', value: 'hasMobile:true' },
    { label: 'No Mobile', value: 'hasMobile:false' },
]

const PAGE_SIZE = 6
const SEARCH_DEBOUNCE_MS = 400

function getInitials(value: string) {
    const cleaned = (value || '').trim()
    if (!cleaned) return ''
    const parts = cleaned.split(/\s+/)
    if (parts.length === 1) return cleaned.slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
}

function parseFilterValue(filters: string[], category: string): boolean | undefined {
    const match = filters.find((f) => f.startsWith(`${category}:`))
    if (!match) return undefined
    return match.split(':')[1] === 'true'
}

// Adds one day so a picked "To" date includes the entirety of that day
// (backend upper bound is exclusive).
function toExclusiveEndOfDay(date: Date): string {
    const d = new Date(date)
    d.setDate(d.getDate() + 1)
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
}

function formatRangeLabel(range?: DateRange) {
    if (!range?.from) return 'Any date'
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    if (range.to) return `${fmt(range.from)} - ${fmt(range.to)}`
    return fmt(range.from)
}

const InstitutionList: React.FC<InstitutionListProps> = ({ onCreate }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const { getInstitutions } = useContext(ConfigurationApiContext)

    const [data, setData] = useState<InstitutionListItem[]>([])
    const [meta, setMeta] = useState<InstitutionsMeta | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [selectedFilters, setSelectedFilters] = useState<string[]>([])
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)
    const [page, setPage] = useState(1)

    const hasActiveFilters = selectedFilters.length > 0 || !!dateRange?.from

    // Enforce a single selection per category (e.g. picking "Inactive"
    // replaces "Active" instead of stacking both).
    const handleFilterChange = (values: string[]) => {
        const added = values.find((v) => !selectedFilters.includes(v))
        if (!added) {
            setSelectedFilters(values)
            return
        }
        const category = added.split(':')[0]
        setSelectedFilters(values.filter((v) => v === added || v.split(':')[0] !== category))
    }

    // Debounce raw search input before it drives a network call.
    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timeout)
    }, [search])

    // Any filter change resets to page 1.
    useEffect(() => {
        setPage(1)
    }, [debouncedSearch, selectedFilters, dateRange])

    const fetchInstitutions = async () => {
        setIsLoading(true)
        try {
            const { items, meta: resultMeta } = await getInstitutions({
                Search: debouncedSearch || undefined,
                IsActive: parseFilterValue(selectedFilters, 'isActive'),
                HasWeb: parseFilterValue(selectedFilters, 'hasWeb'),
                HasMobile: parseFilterValue(selectedFilters, 'hasMobile'),
                From: dateRange?.from ? dateRange.from.toISOString() : undefined,
                To: dateRange?.to ? toExclusiveEndOfDay(dateRange.to) : undefined,
                PageLimit: PAGE_SIZE,
                PageNumber: page,
            })

            setData(items)
            setMeta(resultMeta)
        } finally {
            setIsLoading(false)
        }
    }

    const [isDownloading, setIsDownloading] = useState(false)
    const handleDownload = async () => {
        setIsDownloading(true)
        try {
            const { items: exportItems } = await getInstitutions({
                Search: debouncedSearch || undefined,
                IsActive: parseFilterValue(selectedFilters, 'isActive'),
                HasWeb: parseFilterValue(selectedFilters, 'hasWeb'),
                HasMobile: parseFilterValue(selectedFilters, 'hasMobile'),
                From: dateRange?.from ? dateRange.from.toISOString() : undefined,
                To: dateRange?.to ? toExclusiveEndOfDay(dateRange.to) : undefined,
                Download: true,
                FetchAll: true,
            })

            if (!exportItems.length) return

            const csv = buildInstitutionsCsv(exportItems)
            const stamp = new Date().toISOString().slice(0, 10)
            downloadCsv(`institutions-${stamp}.csv`, csv)
        } finally {
            setIsDownloading(false)
        }
    }

    useEffect(() => {
        fetchInstitutions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, selectedFilters, dateRange, page])

    // After creating an institution, CreateInstitution navigates back here
    // with { refresh: true } — the create endpoint doesn't echo the record,
    // so we just refetch page 1 from the server rather than guess its shape.
    useEffect(() => {
        if ((location.state as any)?.refresh) {
            navigate(location.pathname, { replace: true, state: null })
            if (page === 1) {
                fetchInstitutions()
            } else {
                setPage(1)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state])

    const columns: TableColumn<InstitutionListItem>[] = [
        {
            key: 'serial',
            title: 'S/N',
            width: '6rem',
            render: (_row, index) => (
                <Text fontSize="1.4" color={tokens.textMuted}>
                    {(page - 1) * PAGE_SIZE + index + 1}
                </Text>
            ),
        },
        {
            key: 'institution',
            title: 'Institution',
            render: (row: InstitutionListItem) => (
                <Flex gap="1.2" items="center">
                    <Box
                        width="4"
                        height="4"
                        radius="999"
                        bgColor={tokens.primarySoft}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                        }}
                    >
                        {row.logo ? (
                            <img
                                src={row.logo}
                                alt={row.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <Text fontSize="1.2" fontWeight="600" color={tokens.primaryDark}>
                                {getInitials(row.alias || row.name)}
                            </Text>
                        )}
                    </Box>
                    <Text fontWeight="600">{row.name}</Text>
                </Flex>
            ),
        },
        {
            key: 'bankone',
            title: 'BankOne Code',
            dataIndex: 'bankOneCode',
        },
        {
            key: 'ibcode',
            title: 'IB Institution Code',
            dataIndex: 'institutionCode',
        },
        {
            key: 'alias',
            title: 'Alias',
            dataIndex: 'alias',
        },
        {
            key: 'setup',
            title: 'Setup Date',
            dataIndex: 'setupDate',
        },
        {
            key: 'status',
            title: 'Status',
            render: (row: InstitutionListItem) => (
                <span
                    style={{
                        display: 'inline-block',
                        padding: '0.2rem 1rem',
                        borderRadius: '9999px',
                        fontSize: '1.3rem',
                        fontWeight: 600,
                        background: row.isActive ? '#ECFDF3' : '#F2F4F7',
                        color: row.isActive ? '#027A48' : '#667085',
                    }}
                >
            {row.isActive ? 'Active' : 'Inactive'}
        </span>
            ),
        },
        {
            key: 'action',
            title: '',
            width: '13rem',
            render: (row: InstitutionListItem) => (
                <Text
                    fontSize="1.4"
                    fontWeight="600"
                    color={tokens.primary}
                    className="institution-list__view-link"
                    style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                    onClick={() => navigate(`/dashboardv2/institution-details?institutionId=${row.id}`)}
                >
                    View Details
                </Text>
            ),
        },
    ]
    return (
        <div style={{ padding: '2.4rem 3.2rem' }}>
                    <Flex justify="space-between" items="center">
                        <Text fontSize="2.4" fontWeight="700" color={tokens.textDark}>
                            Institutions
                        </Text>

                        <Button onClick={onCreate}>
                            Create Institution
                        </Button>
                    </Flex>

                    <Box
                        topMargin="2.4"
                        bgColor={tokens.white}
                        borderColor={tokens.border}
                        radius="1.2"
                        shadow={tokens.shadowCard}
                        style={{ padding: '0 1.6rem' }}
                    >
                        <Toolbar
                            left={
                                <>
                                    <SearchField
                                        width="32rem"
                                        value={search}
                                        onChange={setSearch}
                                        placeholder="Search"
                                    />

                                    <FilterDropdown
                                        label="Filters"
                                        options={FILTER_OPTIONS}
                                        value={selectedFilters}
                                        onChange={handleFilterChange}
                                        footer={
                                            <div className="institution-list__dropdown-date">
                                                <Text fontSize="1.3" fontWeight="600" color={tokens.textMuted}>
                                                    Setup date range
                                                </Text>
                                                <DateRangeFilterButton value={dateRange} onChange={setDateRange} />
                                            </div>
                                        }
                                    />

                                    <Button
                                        variant="outline"
                                        disabled={!hasActiveFilters}
                                        onClick={() => {
                                            setSelectedFilters([])
                                            setDateRange(undefined)
                                        }}
                                    >
                                        Clear Filters
                                    </Button>
                                </>
                            }
                            right={
                                <button
                                    type="button"
                                    className="institution-list__download-btn"
                                    onClick={handleDownload}
                                    disabled={isDownloading}
                                >
                                    <img src={downloadIcon} alt="" width={16} height={16} />
                                    {isDownloading ? 'Downloading...' : 'Download'}
                                </button>
                            }
                        />
                    </Box>

                    <Box
                        topMargin="1.6"
                        bgColor={tokens.white}
                        borderColor={tokens.border}
                        radius="1.2"
                        shadow={tokens.shadowCard}
                        style={{ overflow: 'hidden' }}
                    >
                        <InstitutionTable columns={columns} data={data} loading={isLoading} />

                        <div style={{ borderTop: '1px solid #EAECF0' }} />

                        <Box padding="1.6">
                            <TablePagination
                                page={page}
                                total={meta?.total ?? 0}
                                pageSize={PAGE_SIZE}
                                onPageChange={setPage}
                            />
                        </Box>
                    </Box>
                </div>
    )
}

export default InstitutionList